import { useContext, useMemo } from "react";
import BorseContext from "../../context/borse";
import Requests from "../requests";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/it";
import "dayjs/locale/fr";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import useBorseRequestDeleteMutation from "../../hooks/mutations/useBorseRequestDeleteMutation";
import useBorseRequestCompleteMutation from "../../hooks/mutations/useBorseRequestCompleteMutation";
dayjs.extend(LocalizedFormat);
dayjs.extend(localeData);

const BorseRequests = () => {
  const {
    labels,
    campaign,
    requests: currentRequests,
    setCurrentRequest,
    appLang,
    isRequestFetching,
    status,
  } = useContext(BorseContext);

  const sortingOptions = useMemo(
    () => [
      {
        value: "STUDENT_AZ",
        label: labels?.WELFARE_BORSE_STUDENT_NAME_AZ || "Student name AZ",
        desc: true,
      },
      {
        value: "STUDENT_ZA",
        label: labels?.WELFARE_BORSE_STUDENT_NAME_ZA || "Student name ZA",
        desc: false,
      },
      { value: "SCHOOL_AZ", label: labels?.WELFARE_BORSE_SCHOOL_AZ || "School AZ", desc: true },
      { value: "SCHOOL_ZA", label: labels?.WELFARE_BORSE_SCHOOL_ZA || "School ZA", desc: false },
      { value: "DATE_AZ", label: labels?.WELFARE_BORSE_NEWEST || "Newest", desc: true },
      { value: "DATE_ZA", label: labels?.WELFARE_BORSE_OLDEST || "Oldest", desc: false },
      { value: "STATUS_AZ", label: labels?.WELFARE_BORSE_STATUS_AZ || "Status AZ", desc: true },
      { value: "STATUS_ZA", label: labels?.WELFARE_BORSE_STATUS_ZA || "Status ZA", desc: false },
    ],
    [labels]
  );

  const columnItemTitle = useMemo(
    () => [
      {
        label: labels?.WELFARE_BORSE_STUDENT_NAME || "Student name",
        className: "table__box--big",
        name: "STUDENT",
        getContent: (request) => (
          <span translate="no">
            {`${request?.studentFirstName || "-"} ${request?.studentLastName || ""}`}
          </span>
        ),
      },
      {
        label: labels?.WELFARE_BORSE_SCHOOL || "School",
        className: "table__box--big",
        name: "SCHOOL",
        getContent: (request) => `${request?.school || "-"}`,
      },
      {
        label: labels?.WELFARE_BORSE_DATE || "Date",
        name: "DATE",
        getContent: (request) =>
          dayjs(Number(request?.creationDate)).locale(appLang?.value).format("L"),
      },
      {
        label: labels?.WELFARE_BORSE_STATUS || "Status",
        name: "STATUS",
        getContent: (request) => (
          <span className={`table__status table__status--${request?.status.toLowerCase()}`}>
            {labels?.[`WELFARE_STATUS_${request?.status.toUpperCase()}`] || request.status}
          </span>
        ),
      },
    ],
    [labels, appLang]
  );

  const { mutateAsync: deleteRequest, isError, reset, isLoading } = useBorseRequestDeleteMutation();

  const handleDeleteRequest = async (request) => {
    await deleteRequest({ campaignId: campaign?.id, request });
  };

  const { mutateAsync: submitRequest, isError: isSubmitError } = useBorseRequestCompleteMutation();

  const handleSubmitRequest = async (request) => {
    await submitRequest({ request });
  };

  return (
    status !== "closed" && (
      <div className="app-content__section borse-content__requests">
        {isRequestFetching ? (
          <div className="loading-spinner" />
        ) : currentRequests?.length > 0 ? (
          <>
            <div className="app-content__title-wrapper">
              <h2 className="app-content__title">
                {(labels?.WELFARE_BORSE_REQUEST_CURRENT_YEAR || "Requests [YEAR]").replace(
                  "[YEAR]",
                  campaign?.name
                )}
              </h2>
            </div>
            <Requests
              currentRequests={currentRequests}
              setCurrentRequest={setCurrentRequest}
              labels={labels}
              sortingOptions={sortingOptions}
              columnItemTitle={columnItemTitle}
              clickableCondition={["STUDENT", "SCHOOL"]}
              hideSendCondition={(request) => request?.lastCompletedStep !== 2}
              deleteObj={{
                deleteRequest: handleDeleteRequest,
                isError: isError,
                reset: reset,
                isLoading: isLoading,
              }}
              submitObj={{
                submitRequest: handleSubmitRequest,
                isError: isSubmitError,
              }}
            />
          </>
        ) : (
          <div
            className="error"
            dangerouslySetInnerHTML={{
              __html:
                status === "request-phase"
                  ? labels?.WELFARE_BORSE_NO_REQUESTS_YET || "No requests yet"
                  : labels?.WELFARE_BORSE_NO_REQUESTS_YET_CLOSED ||
                    "Dear colleague, the initiative is currently closed. We will inform you when it will be possible to apply for the scholarships.",
            }}
          />
        )}
      </div>
    )
  );
};

export default BorseRequests;
